import { cn } from '@invent/wl-ui-kit-next';
import type * as React from 'react';

import { StatusChip } from '@/design';
import type { IMyTaskFlags, MyTaskFlag } from '../../api/work-item-contracts';

/**
 * My tasks' row marks (SR-485), from survey-api's row `flags`: an overdue row tinted with a danger
 * edge beside its Overdue chip, a "Today" chip on one due today, a "New" mark by the title of one
 * assigned since the viewer's last visit — always; and the mark of each flag the list is narrowed
 * to, where it has no other ("No outreach"), so the table stays calm the rest of the time. Every
 * mark is words as well as colour. My tasks draws them; the work items' table only where its
 * caller hands it the flags (`WorkItemsTable`'s `marks`). `data-overdue`, `data-due-today`,
 * `data-new` on the row and `data-flag-mark` by its title are where a spec finds them.
 */
export function markRowProps(
  flags: Nullable<IMyTaskFlags> | undefined
): React.HTMLAttributes<HTMLTableRowElement> {
  if (!flags) return {};
  return {
    ...(flags.overdue && { 'data-overdue': '' }),
    ...(flags.dueToday && { 'data-due-today': '' }),
    ...(flags.isNew && { 'data-new': '' }),
    className: flags.overdue
      ? 'bg-[color-mix(in_srgb,var(--color-magenta)_4%,white)] shadow-[inset_3px_0_0_var(--color-deep-magenta)]'
      : undefined
  } as React.HTMLAttributes<HTMLTableRowElement>;
}

/**
 * Whether a task draws *Today* (SR-525, E2) — one rule for every card: a due already passed is
 * *Overdue* alone, and *Today* is said only while today's due is still ahead. `at` is a booking's
 * instant, checked against the viewer's clock; a due day has none, and is ahead all day.
 */
export function todayShown(overdue: boolean, dueToday: boolean | undefined, at?: Nullable<string>): boolean {
  if (overdue || !dueToday) return false;
  const time = at ? Date.parse(at) : NaN;
  return Number.isNaN(time) || time >= Date.now();
}

/** Beside a due day or a booked call due today. */
export function TodayChip() {
  return (
    <span data-today-chip="">
      <StatusChip tone="attention">Today</StatusChip>
    </span>
  );
}

/** By a task's title: assigned since the viewer's last visit. */
export function NewMark() {
  return (
    <span
      data-new-mark=""
      className={cn(
        'ml-1.5 inline-flex items-center gap-1 align-middle',
        'text-[0.6875rem] font-medium text-[var(--color-royal-blue)]'
      )}
    >
      <span aria-hidden className="size-1.5 rounded-full bg-[var(--color-royal-blue)]" />
      New<span className="sr-only"> since your last visit</span>
    </span>
  );
}

/** The flags whose only mark is the one drawn while the list is narrowed to them, in its words. */
const FLAG_MARKS: Partial<Record<MyTaskFlag, { words: string; raised: (flags: IMyTaskFlags) => boolean }>> = {
  no_outreach: { words: 'No outreach', raised: (flags) => flags.noOutreach === true },
  edited_this_cycle: { words: 'Edited this cycle', raised: (flags) => flags.editedThisCycle === true },
  not_asked: { words: 'Not asked', raised: (flags) => flags.notAsked === true },
  asked_no_reply: { words: 'Asked, no reply', raised: (flags) => flags.askedNoReply === true }
};

/** By a task's title: each flag the list is narrowed to that the row raises. */
export function FlagMarks({
  narrowed,
  flags
}: {
  narrowed: readonly string[];
  flags: Nullable<IMyTaskFlags> | undefined;
}) {
  return narrowed.map((flag) => <FlagMark key={flag} flag={flag} flags={flags} />);
}

function FlagMark({ flag, flags }: { flag: string; flags: Nullable<IMyTaskFlags> | undefined }) {
  const mark = FLAG_MARKS[flag as MyTaskFlag];
  if (!mark || !flags || !mark.raised(flags)) return null;
  return (
    <span
      data-flag-mark={flag}
      className={cn(
        'ml-1.5 inline-flex h-4.5 items-center rounded-full px-1.5 align-middle',
        'bg-muted text-[0.6875rem] font-medium text-muted-foreground'
      )}
    >
      {mark.words}
    </span>
  );
}
